import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import SharedFolderCommentsPanel from '../../comments/SharedFolderCommentsPanel';
import SharedFolderChatPanel from '../../chat/SharedFolderChatPanel';
import { sharedFolderChatUnreadCount, sharedKeys } from '../sharedAccessApi';

const TABS = [
  { key: 'comments', label: 'Comments' },
  { key: 'chat', label: 'Chat' },
];

/** Folder counterpart to {@link SharedConversationSidebar} — only rendered when `meta.canChat`. */
export default function SharedFolderConversationSidebar({ folderId }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('comments');
  const [visited, setVisited] = useState({ comments: true, chat: false });

  // Polled independently of the chat panel itself, so a message that arrives
  // while the Chat tab is closed (or never opened) still raises a bubble here.
  const unreadKey = sharedKeys.folderUnread(folderId);
  const { data: chatUnread = 0 } = useQuery({
    queryKey: unreadKey,
    queryFn: () => sharedFolderChatUnreadCount(folderId),
    enabled: Boolean(folderId),
    refetchInterval: 15_000,
  });

  const select = (key) => {
    setTab(key);
    setVisited((v) => ({ ...v, [key]: true }));
    if (key === 'chat') queryClient.invalidateQueries({ queryKey: unreadKey });
  };

  return (
    <aside className="flex w-[360px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-slate-100">
      <div className="flex shrink-0 border-b border-slate-200">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => select(key)}
            className={clsx(
              'flex-1 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === key
                ? 'border-brand-500 text-slate-800'
                : 'border-transparent text-slate-400 hover:text-slate-600',
            )}
          >
            {label}
            {key === 'chat' && tab !== 'chat' && chatUnread > 0 && (
              <span className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                {chatUnread > 9 ? '9+' : chatUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      {visited.comments && (
        <div hidden={tab !== 'comments'} className="min-h-0 flex-1 p-3">
          <SharedFolderCommentsPanel folderId={folderId} />
        </div>
      )}
      {visited.chat && (
        <div hidden={tab !== 'chat'} className="min-h-0 flex-1">
          <SharedFolderChatPanel folderId={folderId} />
        </div>
      )}
    </aside>
  );
}
